/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback } from "react";
import { logger } from "@/utils/logger";
import { motion, AnimatePresence } from "framer-motion";
import { Home, RotateCcw, Star, Volume2, VolumeX, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import Confetti from "react-confetti";
import { useWindowSize } from "@/hooks/useWindowSize";
import { savePlayerNames, loadPlayerNames } from "@/utils/playerStorage";
import { recordGameWon } from "@/utils/playerProfile";
import { TEBAK_CATEGORIES as CATEGORIES } from "@/utils/quizData";

import { getVoiceLang } from "@/utils/voicePref";
const speakText = (t) => {
  try { if (!window.speechSynthesis) return; window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(t); u.lang = getVoiceLang(); u.rate=0.85; u.pitch=1.0; window.speechSynthesis.speak(u);
  } catch (err) { logger.debug("speakText failed", err); }
};

const TebakGambar = ({ onBackToMenu }) => {
  const { width: winW, height: winH } = useWindowSize();
  const [gameState, setGameState] = useState("menu");
  const [playerNames, setPlayerNames] = useState(() => { const s = loadPlayerNames(); return [s[0]||"",s[1]||""]; });
  const [currentPlayer, setCurrentPlayer] = useState(0);
  const [scores, setScores] = useState([0, 0]);
  const [round, setRound] = useState(0);
  const [totalRounds] = useState(12);
  const [currentItem, setCurrentItem] = useState(null);
  const [blurLevel, setBlurLevel] = useState(30);
  const [options, setOptions] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [soundOn, setSoundOn] = useState(true);
  const [category, setCategory] = useState("hewan");
  const [timeLeft, setTimeLeft] = useState(10);
  const [timerActive, setTimerActive] = useState(false);

  const p1 = playerNames[0] || "Pemain 1";
  const p2 = playerNames[1] || "Pemain 2";

  const startGame = () => { savePlayerNames(playerNames); setScores([0,0]); setRound(0); setCurrentPlayer(0); setGameState("playing"); };

  const nextRound = useCallback(() => {
    const items = CATEGORIES[category].items;
    const correct = items[Math.floor(Math.random() * items.length)];
    const wrongs = items.filter(i => i.name !== correct.name).sort(() => Math.random()-0.5).slice(0,3);
    const opts = [...wrongs, correct].sort(() => Math.random()-0.5);
    setCurrentItem(correct);
    setOptions(opts);
    setBlurLevel(30);
    setFeedback(null);
    setTimeLeft(10);
    setTimerActive(true);
    if (soundOn) speakText(`Turn ${currentPlayer === 0 ? p1 : p2}! Tebak gambar ini!`);
  }, [category, currentPlayer, p1, p2, soundOn]);

  useEffect(() => { if (gameState === "playing") nextRound(); }, [gameState]);
  useEffect(() => { if (gameState === "playing" && round > 0) nextRound(); }, [round]);

  // Blur reveal timer
  useEffect(() => {
    if (!timerActive || gameState !== "playing") return;
    const id = setInterval(() => {
      setBlurLevel(prev => Math.max(0, prev - 3));
      setTimeLeft(prev => {
        if (prev <= 1) { setTimerActive(false); handleAnswer(null); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [timerActive, gameState]);

  const handleAnswer = (item) => {
    setTimerActive(false);
    if (item && item.name === currentItem.name) {
      setFeedback({ type: "correct", text: `Benar! Itu ${currentItem.name}!` });
      setScores(prev => { const n = [...prev]; n[currentPlayer] += 1; return n; });
      if (soundOn) speakText(`Correct! That's ${currentItem.name}!`);
    } else {
      setFeedback({ type: "wrong", text: `Itu ${currentItem.name}!` });
      if (soundOn) speakText(`The answer is ${currentItem.name}!`);
    }
    setBlurLevel(0);
    setTimeout(() => {
      if (round + 1 >= totalRounds) {
        setGameState("won");
        recordGameWon("tebak");
        if (soundOn) {
          const winner = scores[0] > scores[1] ? p1 : scores[1] > scores[0] ? p2 : "Seri";
          setTimeout(() => speakText(`Game over! ${winner === "Draw" ? "Draw!" : winner + " wins!"}`), 500);
        }
      } else {
        setRound(prev => prev + 1);
        setCurrentPlayer(prev => prev === 0 ? 1 : 0);
      }
    }, 2000);
  };

  if (gameState === "menu") {
    return (
      <div className="flex flex-col items-center w-full max-w-2xl mx-auto px-4 pt-4" data-testid="tebak-gambar-menu">
        <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="glass-card-strong rounded-2xl p-6 w-full text-center mb-4">
          <motion.span className="text-6xl block mb-3" animate={{ scale: [1,1.1,1] }} transition={{ duration: 2, repeat: Infinity }}>🔍</motion.span>
          <h1 className="font-fredoka text-3xl text-gold font-bold mb-2">Tebak Gambar</h1>
          <p className="font-fredoka text-gold-light/80 text-sm mb-4">Gambar muncul blur, tebak sebelum waktu habis!</p>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <input data-testid="tebak-p1" type="text" value={playerNames[0]} onChange={e => setPlayerNames([e.target.value, playerNames[1]])} placeholder="Pemain 1" maxLength={15} className="glass-card border-gold/20 rounded-xl px-3 py-2 text-white font-fredoka text-sm focus:outline-none" />
            <input data-testid="tebak-p2" type="text" value={playerNames[1]} onChange={e => setPlayerNames([playerNames[0], e.target.value])} placeholder="Pemain 2" maxLength={15} className="glass-card border-gold/20 rounded-xl px-3 py-2 text-white font-fredoka text-sm focus:outline-none" />
          </div>
          <div className="flex gap-2 mb-4 justify-center flex-wrap">
            {Object.entries(CATEGORIES).map(([k, v]) => (
              <Button key={k} onClick={() => setCategory(k)} className={`font-fredoka text-xs rounded-lg ${category === k ? 'bg-gold text-brown-dark' : 'glass-card text-gold'}`}>{v.name}</Button>
            ))}
          </div>
          <Button onClick={startGame} className="w-full h-12 font-fredoka font-bold text-lg rounded-xl bg-gradient-to-r from-purple-500 to-pink-500" data-testid="tebak-start">MULAI TEBAK!</Button>
        </motion.div>
        <Button onClick={onBackToMenu} variant="outline" className="w-full glass-card border-gold/20 text-gold hover:bg-gold/10 font-fredoka rounded-xl"><Home className="w-4 h-4 mr-2" />Menu</Button>
      </div>
    );
  }

  if (gameState === "won") {
    const winner = scores[0] > scores[1] ? p1 : scores[1] > scores[0] ? p2 : null;
    return (
      <div className="flex flex-col items-center justify-center min-h-[80vh] px-4" data-testid="tebak-gambar-win">
        <Confetti width={winW} height={winH} recycle={false} numberOfPieces={250} />
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="glass-card-strong rounded-2xl p-8 text-center max-w-md w-full">
          <span className="text-6xl block mb-3">🏆</span>
          <h2 className="font-fredoka text-3xl text-gold font-bold mb-2">{winner ? `${winner} Menang!` : "Seri!"}</h2>
          <p className="font-fredoka text-gold-light/80 mb-4">{p1}: {scores[0]} | {p2}: {scores[1]}</p>
          <div className="flex flex-col gap-2">
            <Button onClick={() => { setGameState("playing"); setScores([0,0]); setRound(0); setCurrentPlayer(0); }} className="w-full font-fredoka font-bold rounded-xl h-10 bg-gradient-to-r from-yellow-400 to-amber-500"><RotateCcw className="w-4 h-4 mr-2" />Main Lagi</Button>
            <Button onClick={() => setGameState("menu")} variant="outline" className="w-full glass-card border-gold/20 text-gold font-fredoka rounded-xl">Menu</Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center w-full max-w-2xl mx-auto px-2" data-testid="tebak-gambar-game">
      <div className="w-full glass-card-strong rounded-xl p-2 mb-2 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <span className="font-fredoka text-gold font-bold text-sm">🔍 Ronde {round+1}/{totalRounds}</span>
          <span className="font-fredoka text-cyan-400 text-xs">{p1}: {scores[0]}</span>
          <span className="font-fredoka text-pink-400 text-xs">{p2}: {scores[1]}</span>
        </div>
        <div className="flex gap-1.5">
          <Button onClick={() => setSoundOn(!soundOn)} variant="outline" size="sm" className="glass-card border-gold/20 text-gold h-7 w-7 p-0">{soundOn ? <Volume2 className="w-3 h-3" /> : <VolumeX className="w-3 h-3" />}</Button>
          <Button onClick={() => setGameState("menu")} variant="outline" size="sm" className="glass-card border-gold/20 text-gold h-7 px-2"><Home className="w-3 h-3" /></Button>
        </div>
      </div>

      <div className="font-fredoka text-sm text-center mb-2">
        <span className={currentPlayer === 0 ? "text-cyan-400 font-bold" : "text-pink-400 font-bold"}>Giliran {currentPlayer === 0 ? p1 : p2}</span>
        <span className="text-gold-light/60 ml-2">⏱️ {timeLeft}s</span>
      </div>

      {currentItem && (
        <motion.div className="w-48 h-48 sm:w-56 sm:h-56 rounded-2xl flex items-center justify-center mb-4 mx-auto"
          style={{ background: 'rgba(255,255,255,0.1)', filter: `blur(${blurLevel}px)`, transition: 'filter 0.5s' }}>
          <span className="text-8xl sm:text-9xl">{currentItem.emoji}</span>
        </motion.div>
      )}

      <AnimatePresence>
        {feedback && (
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ opacity: 0 }} className="mb-3">
            <span className={`font-fredoka font-bold text-lg px-6 py-2 rounded-full ${feedback.type === 'correct' ? 'bg-green-500 text-white' : 'bg-red-400 text-white'}`}>{feedback.text}</span>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-2 gap-3 w-full max-w-md">
        {options.map((opt, i) => (
          <motion.button key={`${opt.name}-${i}`} data-testid={`tebak-option-${i}`}
            onClick={() => !feedback && handleAnswer(opt)} disabled={!!feedback}
            className="ingredient-3d rounded-2xl p-4 text-center font-fredoka font-bold text-base sm:text-lg"
            whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.9 }}>
            <span className="text-3xl block mb-1">{opt.emoji}</span>
            {opt.name}
          </motion.button>
        ))}
      </div>
    </div>
  );
};

export default TebakGambar;
